import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "preact/hooks";
import type { CallRequestFormData } from "../components/CallRequestForm";
import { startCall } from "../stores/voice";
import { getRecaptchaToken } from "../utils/recaptcha";
import { type PageParams, TRACKED_PARAMS } from "./usePageParams";

export function useCallRequest(urlParams: PageParams) {
	const [formData, setFormData] = useState<CallRequestFormData>({
		firstName: "",
		lastName: "",
		phone: "",
		email: "",
		message: "",
		consent: false,
	});
	const [error, setError] = useState<string | null>(null);

	const mutation = useMutation({
		mutationFn: async (payload: CallRequestFormData) => {
			const recaptchaToken = await getRecaptchaToken();

			if (!urlParams.campaign_id) {
				throw new Error("Missing URL parameters");
			}

			const attribution: Record<string, string> = {};
			for (const key of TRACKED_PARAMS) {
				const value = urlParams[key];
				if (value) attribution[key] = value;
			}

			return startCall({
				campaign_id: urlParams.campaign_id,
				first_name: payload.firstName,
				last_name: payload.lastName,
				phone: payload.phone,
				country_code: "+1",
				email: payload.email,
				message: payload.message || undefined,
				recaptcha_token: recaptchaToken,
				attribution,
			});
		},
		onSuccess: (response) => {
			console.log("API Response:", response);
		},
		onError: (err) => {
			console.error("Error submitting form:", err);
			setError("An error occurred, try again later");
		},
	});

	useEffect(() => {
		if (!error) {
			return;
		}

		const timer = setTimeout(() => {
			setError(null);
		}, 60000);

		return () => clearTimeout(timer);
	}, [error]);

	const onFieldChange = (
		field: keyof CallRequestFormData,
		value: string | boolean,
	) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};

	const onSubmit = async (e: Event) => {
		e.preventDefault();

		if (!urlParams.campaign_id) {
			console.error("Missing URL parameters");
			return;
		}

		setError(null);
		await mutation.mutateAsync(formData);
	};

	return {
		formData,
		submitted: mutation.isSuccess,
		wasMessageSubmission: Boolean(mutation.variables?.message),
		isLoading: mutation.isPending,
		error,
		onFieldChange,
		onSubmit,
	};
}
